import React, { useImperativeHandle, useRef } from "react";
import { Handler, useGesture } from "@use-gesture/react";
import { ActiveBox } from "../active-box";
import { BottomMenuPanel } from "../bottom-menu-panel";
import { BottomMenuButtons, IBottomMenuButtonsRef } from "../bottom-menu/buttons";
import { PlaceholderImage } from "../placeholder";

import "./layouts-panel.css";

export interface ILayoutPanelItem {
	id: string;
	isSinglePage: boolean;
	leftPageThumbUrl?: string;
	leftPageAspectRatio?: number;
	rightPageThumbUrl?: string;
	rightPageAspectRatio?: number;
}

export interface ILayoutsPanelRef {
	/** Scroll a layout into view */
	scrollIntoView(id: string | undefined, animate?: boolean): void;
	getLayoutSelectionPanelEl(): HTMLElement;
}

interface ILayoutsPanelProps {
	items: readonly ILayoutPanelItem[];
	selectedItemIds?: string[];
	onClick?(id: string): void;
	onDrag?: Handler<"drag">;
}

const MultiPageThumb: React.FC<{
	aspectRatio?: number;
	isSelected: boolean;
	leftPageThumbUrl?: string;
	rightPageThumbUrl?: string;
}> = ({ aspectRatio, isSelected, leftPageThumbUrl, rightPageThumbUrl }) => {
	return (
		<ActiveBox className="layouts-panel__img-container" isActive={isSelected}>
			<div className="spread-thumb-container">
				{leftPageThumbUrl ? (
					<PlaceholderImage
						className="layouts-panel__img__left"
						src={leftPageThumbUrl}
						loading="lazy"
						style={{
							aspectRatio,
						}}
					/>
				) : (
					<div
						className="layouts-panel__img__left"
						style={{
							backgroundColor: "#fff",
							aspectRatio,
						}}
					/>
				)}
				{rightPageThumbUrl ? (
					<PlaceholderImage
						className="layouts-panel__img__right"
						src={rightPageThumbUrl}
						loading="lazy"
						style={{
							aspectRatio,
						}}
					/>
				) : (
					<div
						className="layouts-panel__img__right"
						style={{
							backgroundColor: "#fff",
							aspectRatio,
						}}
					/>
				)}
			</div>
		</ActiveBox>
	);
};

const SinglePageThumb: React.FC<{
	thumbUrl: string;
	isSelected: boolean;
	aspectRatio?: number;
}> = ({ aspectRatio, thumbUrl, isSelected }) => {
	return (
		<ActiveBox className="layouts-panel__img-container" isActive={isSelected}>
			<PlaceholderImage
				className="layouts-panel__img"
				src={thumbUrl}
				loading="lazy"
				style={{
					aspectRatio,
				}}
			/>
		</ActiveBox>
	);
};

export const LayoutsPanel = ({
	items,
	selectedItemIds,
	onClick,
	onDrag,
	ref,
}: ILayoutsPanelProps & { ref?: React.Ref<ILayoutsPanelRef> }) => {
	const gestures = useGesture(
		{
			onDrag,
		},
		{
			drag: {
				pointer: { touch: true },
				preventScrollAxis: "x",
				axis: "y",
			},
		},
	);
	const layoutSelectionPanelRef = useRef<HTMLDivElement>(null);
	const scrollableRef = useRef<IBottomMenuButtonsRef>(null);
	useImperativeHandle(
		ref,
		() => ({
			scrollIntoView: (id: string | undefined, animate = true) => {
				if (!scrollableRef.current) return;
				const index = items.findIndex((item) => item.id === id);
				scrollableRef.current.scrollIntoView(index, { animate });
			},
			getLayoutSelectionPanelEl: () => {
				return layoutSelectionPanelRef.current as HTMLElement;
			},
		}),
		[items],
	);

	return (
		<BottomMenuPanel className="layouts-panel">
			<BottomMenuButtons
				scrollableClassName="layouts-panel__items"
				containerClassName="layouts-panel__items-container"
				ref={scrollableRef}
			>
				{items.map((item) => {
					return (
						// eslint-disable-next-line jsx-a11y/click-events-have-key-events
						<div
							key={item.id}
							role="button"
							tabIndex={0}
							className="layouts-panel__img-clickable"
							{...gestures(item)}
							onClick={(e) => {
								e.stopPropagation();
								onClick?.(item.id);
							}}
						>
							{item.isSinglePage ? (
								<SinglePageThumb
									aspectRatio={item.leftPageAspectRatio}
									thumbUrl={item.leftPageThumbUrl ?? ""}
									isSelected={selectedItemIds?.includes(item.id) ?? false}
								/>
							) : (
								<MultiPageThumb
									aspectRatio={
										item.leftPageAspectRatio ?? item.rightPageAspectRatio
									}
									isSelected={selectedItemIds?.includes(item.id) ?? false}
									leftPageThumbUrl={item.leftPageThumbUrl}
									rightPageThumbUrl={item.rightPageThumbUrl}
								/>
							)}
						</div>
					);
				})}
			</BottomMenuButtons>
		</BottomMenuPanel>
	);
};
LayoutsPanel.displayName = "LayoutsPanel";
